import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { ChatPanelTechnician } from "@/components/ChatPanelTechnician";
import { ActiveBuildTimer, PausedTimer } from "@/components/ActiveBuildTimer";
import { TechnicianPreview } from "@/components/TechnicianPreview";
import { PendingBuildsQueue } from "@/components/PendingBuildsQueue";
import { TechnicianTestingPanel } from "@/components/TechnicianTestingPanel";
import { ComponentVerificationCard } from "@/components/ComponentVerificationCard";
import { TapDiagOverlay } from "@/components/TapDiagOverlay";
import { Play, Pause, Square, LogOut, Copy, ExternalLink, Cog, Cpu, HardDrive, MemoryStick, Zap, Package, Fan, CircuitBoard, MessageSquare, User, Radio, ArrowLeft, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const PARTS = [
  ["cpu", "CPU", Cpu], ["gpu", "GPU", Cog], ["motherboard", "MOTHERBOARD", CircuitBoard],
  ["ram", "RAM", MemoryStick], ["storage", "STORAGE", HardDrive],
  ["storage2", "+ SECOND STORAGE", HardDrive],
  ["power_supply", "POWER SUPPLY", Zap],
  ["case", "CASE", Package], ["cpu_cooler", "CPU COOLER", Fan],
  ["fans", "FANS", Fan],
];
const QTY_KEYS = { ram: "ram_qty", storage: "storage_qty", storage2: "storage2_qty", fans: "fans_qty" };

const PROGRESS = ["Waiting for Technician", "Live Assembly", "Assembly Completed", "Testing", "Completed"];

function Card({ children, className="", ...rest }) {
  return <div className={`rounded-xl border p-5 md:p-6 ${className}`} style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10" }} {...rest}>{children}</div>;
}

function BigBtn({ color, disabled, onClick, icon:Icon, label, testid }) {
  const map = {
    green: { bg: "rgba(0,200,83,0.08)", border: "rgba(0,200,83,0.5)", fg: "#00c853" },
    orange: { bg: "rgba(255,152,0,0.08)", border: "rgba(255,152,0,0.5)", fg: "#ff9800" },
    cyan: { bg: "rgba(0,229,255,0.08)", border: "rgba(0,229,255,0.5)", fg: "#00e5ff" },
    red: { bg: "rgba(255,51,51,0.08)", border: "rgba(255,51,51,0.5)", fg: "#ff3333" },
  };
  const c = map[color];
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className="rounded-lg border h-24 flex flex-col items-center justify-center gap-1.5 font-semibold text-[13px] tracking-wide transition-transform hover:-translate-y-[1px] disabled:opacity-30 disabled:cursor-not-allowed"
      style={{
        background: c.bg,
        borderColor: c.border,
        color: c.fg,
        // iOS Safari fix: opts this button out of the double-tap-to-zoom
        // heuristic so a single tap always dispatches click immediately.
        // Without this, WebKit can silently swallow the tap on a button that
        // just transitioned from disabled → enabled (e.g. FINISH ASSEMBLY
        // enabling ~1 s after START), producing no ripple / toast / request.
        touchAction: "manipulation",
        WebkitTapHighlightColor: "rgba(0,0,0,0)",
      }}>
      <Icon size={22} /><span style={{ pointerEvents: "none" }}>{label}</span>
    </button>
  );
}

export default function TechnicianStation() {
  const { logout, user } = useAuth();
  // ?tapdiag=<mode>. See TapDiagOverlay for valid modes.
  // Persisted across the auth-guard redirect (which drops the query string)
  // via localStorage, so a real iPad session keeps the mode.
  const tapDiagMode = React.useMemo(() => {
    if (typeof window === "undefined") return "off";
    const q = new URLSearchParams(window.location.search).get("tapdiag");
    if (q != null) {
      try { window.localStorage.setItem("tapdiag", q); } catch {}
      return q;
    }
    try { return window.localStorage.getItem("tapdiag") || "off"; } catch { return "off"; }
  }, []);
  const [order, setOrder] = useState(null);
  const [pending, setPending] = useState([]);
  const [activeRef, setActiveRef] = useState(null); // {id, order_number, status} when station has LIVE/PAUSED
  const [selectedId, setSelectedId] = useState(null); // technician's chosen WAITING order id
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [technicianName, setTechnicianName] = useState("");
  const [sessionStartedAt, setSessionStartedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      setRefreshing(true);
      const stId = user?.station_id || "ST01";
      const { data: q } = await client.get(`/stations/${stId}/pending-builds`);
      setPending(q?.pending || []);
      setActiveRef(q?.active || null);

      if (q?.active) {
        // LIVE / PAUSED protection — always route to the active build.
        const r = await client.get(`/stations/${stId}/next-order`);
        setOrder(r.data);
        setSelectedId(r.data?.id || null);
        if (r.data?.technician_notes !== undefined) setNotes(r.data.technician_notes || "");
        if (r.data?.start_time && !sessionStartedAt) setSessionStartedAt(r.data.start_time);
      } else if (selectedId) {
        const row = (q?.pending || []).find((p) => p.id === selectedId);
        if (row) {
          // Hydrate a WAITING order with the fields the existing build UI reads.
          setOrder({
            id: row.id,
            order_number: row.order_number,
            customer_name: row.customer_name,
            status: "WAITING",
            components: row.components || {},
            technician_notes: "",
            pause_intervals: [],
          });
        } else {
          setOrder(null);
          setSelectedId(null);
        }
      } else {
        setOrder(null);
      }

      const st = await client.get("/stations");
      const s = (st.data || []).find((x) => x.id === (user?.station_id || "ST01"));
      if (s?.technician_name) setTechnicianName(s.technician_name);
    } catch {} finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedId, sessionStartedAt]);

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const openBuild = (row) => {
    setSelectedId(row.id);
    setOrder({
      id: row.id,
      order_number: row.order_number,
      customer_name: row.customer_name,
      status: "WAITING",
      components: row.components || {},
      technician_notes: "",
      pause_intervals: [],
    });
    setNotes("");
  };

  const backToQueue = () => {
    // Only allowed while the selected order has not been started.
    if (order && order.status !== "WAITING") return;
    setSelectedId(null);
    setOrder(null);
    load();
  };

  const [pauseModal, setPauseModal] = useState(false);
  const [testing, setTesting] = useState(null);
  const [verify, setVerify] = useState({ allVerified: false, verified: 0, total: 0, openIssueCount: 0 });
  const act = async (path, confirmMsg, body) => {
    if (!order) return;
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await client.post(`/orders/${order.id}/${path}`, body || {});
      toast.success(path.toUpperCase().replace(/-/g, " "));
      if (path === "finish") {
        // Return to the queue automatically.
        setSelectedId(null);
        setOrder(null);
        setSessionStartedAt(null);
        setTesting(null);
      }
      load();
    }
    catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.reason || d?.detail || "Action failed"));
    }
    finally { setBusy(false); }
  };
  const requestPause = () => setPauseModal(true);
  const confirmPause = (reason) => { setPauseModal(false); act("pause", null, { reason }); };
  const saveNotes = async () => { if (!order) return; try { await client.post(`/orders/${order.id}/notes`, { notes }); toast.success("Notes saved"); } catch { toast.error("Save failed"); } };

  // Load testing state whenever the order enters TESTING (or transitions in/out).
  useEffect(() => {
    if (!order || (order.status !== "TESTING" && order.status !== "COMPLETED")) {
      setTesting(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get(`/orders/${order.id}/testing`);
        if (!cancelled) setTesting(data);
      } catch { /* not started yet */ }
    })();
    return () => { cancelled = true; };
  }, [order?.id, order?.status]);

  const onSystemMessage = useCallback((p) => {
    if (p.type !== "testing_state" && p.type !== "testing_temp") return;
    // Authoritative refetch on any WS testing_* event. The technician's own
    // clicks apply state immediately from the PUT/POST response body (see
    // TechnicianTestingPanel); this WS-triggered refetch covers changes that
    // originate elsewhere (multi-window, out-of-band admin actions, etc.).
    if (!order?.id) return;
    client.get(`/orders/${order.id}/testing`)
      .then(({ data }) => setTesting(data))
      .catch(() => {});
  }, [order?.id]);

  const status = order?.status || "IDLE";
  const s = { WAITING: {c:"#a1a1aa"}, LIVE:{c:"#00e5ff"}, PAUSED:{c:"#ff9800"}, TESTING:{c:"#00e5ff"}, COMPLETED:{c:"#00ff66"}}[status] || {c:"#a1a1aa"};
  const canStart = status === "WAITING" && verify.allVerified;
  const canPause = status === "LIVE", canResume = status === "PAUSED";
  const canFinishAssembly = status === "LIVE";
  const progIdx = status === "LIVE" || status === "PAUSED" ? 1 : status === "TESTING" ? 3 : status === "COMPLETED" ? 4 : 0;
  const copyOrder = () => order?.order_number && navigator.clipboard.writeText(order.order_number).then(()=>toast.success("Copied"));

  return (
    <div className="customer-live min-h-screen overflow-x-hidden" style={{ background: "#050507" }}>
      {/* Header */}
      <div className="border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/idea-logo.png" alt="IDEA" className="w-[90px] h-auto" data-testid="idea-logo" />
            <span className="text-sm font-semibold tracking-[0.28em]" style={{color:"var(--accent)"}}>BUILD LAB</span>
            <span className="text-white/30">•</span>
            <span className="text-white text-sm font-semibold tracking-wide">STATION 1</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border" style={{ borderColor: "rgba(0,255,102,0.35)", background: "rgba(0,255,102,0.05)" }}>
              <span className="w-2 h-2 rounded-full" style={{background:"#00ff66"}}/><span className="text-[11px] font-semibold tracking-[0.22em] text-white">Station 1 Unlocked</span>
            </div>
            <div className="flex items-center gap-2">
              <User size={16} className="text-white/60"/>
              <div className="leading-tight">
                <div className="text-white text-sm font-semibold" data-testid="tech-name">{technicianName || "—"}</div>
                <div className="text-white/40 text-[10px] tracking-[0.24em] uppercase">Technician</div>
              </div>
            </div>
            <button onClick={logout} className="text-white/50 hover:text-white text-xs" data-testid="tech-logout"><LogOut size={14}/></button>
          </div>
        </div>
      </div>

      {loading ? <div className="p-10 text-white/40">Loading…</div> : !order ? (
        pending.length > 0 && !activeRef ? (
          <PendingBuildsQueue pending={pending} onOpen={openBuild} refreshing={refreshing} />
        ) : (
          <div className="max-w-[1600px] mx-auto p-10">
            <Card><div className="text-white/60 text-center py-10 font-semibold" data-testid="queue-empty-state">Awaiting new build order.</div></Card>
          </div>
        )
      ) : (
        <>
        {order.status === "WAITING" && !activeRef && (
          <div className="max-w-[1600px] mx-auto px-6 pt-4">
            <button
              type="button"
              onClick={backToQueue}
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-white/60 hover:text-white transition-colors"
              data-testid="back-to-queue"
            >
              <ArrowLeft size={14} /> Back to Pending Builds
            </button>
          </div>
        )}
        <div className="max-w-[1600px] mx-auto p-6 grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* LEFT */}
          <div className="xl:col-span-3 space-y-6">
            <Card data-testid="current-build">
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 font-semibold mb-4">Current Build</div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 font-medium">Order</div>
              <div className="flex items-center gap-2 mt-1">
                <div className="text-white text-xl font-semibold font-mono" data-testid="tech-order-number">{order.order_number}</div>
                <button onClick={copyOrder} className="text-white/40 hover:text-white" data-testid="copy-order"><Copy size={14}/></button>
              </div>
              <div className="mt-5 text-[10px] tracking-[0.24em] uppercase text-white/40 font-medium">Customer</div>
              <div className="mt-1 text-white font-medium">{order.customer_name || "—"}</div>
            </Card>
            {order.status === "WAITING" && (
              <ComponentVerificationCard
                orderId={order.id}
                onReady={setVerify}
              />
            )}
          </div>

          {/* CENTER */}
          <div className="xl:col-span-6 space-y-6">
            <TechnicianPreview stationId={user?.station_id || "ST01"} status={status} />

            <Card data-testid="status-card">
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 font-semibold">Status</div>
              <div className="mt-2 text-5xl font-semibold tracking-tight" style={{color:s.c}} data-testid="tech-status">{status}</div>
              <div className="mt-4 text-[10px] tracking-[0.28em] uppercase text-white/50 font-medium">Build Timer (Active)</div>
              <div className="mt-1"><ActiveBuildTimer startTime={order.start_time} finishTime={order.finish_time} status={status} pauseIntervals={order.pause_intervals||[]} currentPauseStartedAt={order.current_pause_started_at} size="xl"/></div>
              <div className="mt-1 text-[11px] text-white/40">Active Time (pauses excluded)</div>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <BigBtn color="green" disabled={!canStart||busy} onClick={()=>act("start")} icon={Play} label="START BUILD" testid="start-build-button"/>
              <BigBtn color="orange" disabled={!canPause||busy} onClick={requestPause} icon={Pause} label="PAUSE BUILD" testid="pause-build-button"/>
              <BigBtn color="cyan" disabled={!canResume||busy} onClick={()=>act("resume")} icon={Play} label="RESUME BUILD" testid="resume-build-button"/>
              <BigBtn color="red" disabled={!canFinishAssembly||busy} onClick={()=>act("finish-assembly","Finish assembly and enter Testing? The livestream stays active.")} icon={Square} label="FINISH ASSEMBLY" testid="finish-assembly-button"/>
            </div>

            {status === "TESTING" && (
              <>
                <TechnicianTestingPanel
                  orderId={order.id}
                  testing={testing}
                  busy={busy}
                  onTestingUpdate={setTesting}
                  onCompleteTesting={() => act("finish","Complete testing and finish the build? Customer access ends immediately.")}
                />
              </>
            )}

            {status === "PAUSED" && (
              <Card className="border-l-4" data-testid="paused-banner">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Pause size={20} style={{color:"#ff9800"}}/>
                    <div>
                      <div className="font-semibold" style={{color:"#ff9800"}}>BUILD PAUSED</div>
                      <div className="text-xs text-white/50 mt-0.5">Timer is paused. Click RESUME BUILD to continue.</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-white/45">Paused Time</div>
                    <div className="mt-0.5"><PausedTimer pauseIntervals={order.pause_intervals||[]} currentPauseStartedAt={order.current_pause_started_at} status={status} size="md"/></div>
                  </div>
                </div>
              </Card>
            )}

            <Card data-testid="build-progress">
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 font-semibold mb-5">Build Progress</div>
              <div className="flex items-start justify-between gap-1">
                {PROGRESS.map((label,i)=>{
                  const done = i < progIdx, active = i === progIdx;
                  return (
                    <React.Fragment key={i}>
                      <div className="flex flex-col items-center w-20">
                        <div className="w-9 h-9 rounded-full border flex items-center justify-center font-semibold text-sm" style={{
                          borderColor: active?"var(--accent)": done?"rgba(0,229,255,0.5)":"rgba(255,255,255,0.18)",
                          color: active?"var(--accent)": done?"rgba(0,229,255,0.9)":"rgba(255,255,255,0.4)",
                          background: active?"rgba(0,229,255,0.1)":"transparent"
                        }}>{i+1}</div>
                        <div className={`mt-2 text-[10px] text-center leading-tight ${active?"":done?"text-white/60":"text-white/40"}`} style={active?{color:"var(--accent)"}:{}}>{label}</div>
                      </div>
                      {i<4 && <div className="flex-1 h-px mt-[18px]" style={{background: i<progIdx?"rgba(0,229,255,0.4)":"rgba(255,255,255,0.1)"}}/>}
                    </React.Fragment>
                  );
                })}
              </div>
            </Card>

            <Card data-testid="notes-card">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 font-semibold">Notes <span className="text-white/30 text-[10px]">(Private)</span></div>
              </div>
              <div className="flex gap-3">
                <input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Add notes about this build…" maxLength={2000} className="flex-1 bg-black border border-white/10 rounded-md px-3 h-10 text-sm text-white placeholder-white/30 focus:outline-none" data-testid="notes-input"/>
                <button onClick={saveNotes} className="px-4 h-10 rounded-md text-[11px] font-semibold tracking-wide border" style={{borderColor:"rgba(0,229,255,0.35)", color:"var(--accent)"}} data-testid="notes-save">SAVE NOTE</button>
              </div>
            </Card>
          </div>

          {/* RIGHT */}
          <div className="xl:col-span-3">
            <ChatPanelTechnician stationId={user?.station_id || "ST01"} order={order} onSystemMessage={onSystemMessage}/>
          </div>
        </div>
        </>
      )}

      {/* Pause Reason Modal */}
      {pauseModal && (
        <PauseReasonModal onCancel={() => setPauseModal(false)} onConfirm={confirmPause} busy={busy} />
      )}

      {/* Footer */}
      {order && (
        <div className="border-t mt-4" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <div className="max-w-[1600px] mx-auto px-6 py-4 grid grid-cols-2 md:grid-cols-6 gap-6 text-xs">
            <Meta label="Station" value={user?.station_id || "ST01"}/>
            <Meta label="Technician" value={technicianName||"—"}/>
            <Meta label="Session Started" value={order.start_time?new Date(order.start_time).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):"—"}/>
            <Meta label="Paused Time" value={<PausedTimer pauseIntervals={order.pause_intervals||[]} currentPauseStartedAt={order.current_pause_started_at} status={status} size="md"/>}/>
            <Meta label="Elapsed Time" value={<ActiveBuildTimer startTime={order.start_time} finishTime={order.finish_time} status={status} pauseIntervals={order.pause_intervals||[]} currentPauseStartedAt={order.current_pause_started_at} size="md"/>}/>
            <Meta label="Last Updated" value={new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}/>
          </div>
        </div>
      )}
      <TapDiagOverlay mode={tapDiagMode} />
    </div>
  );
}

function Meta({label,value}){return <div><div className="text-[10px] tracking-[0.24em] uppercase text-white/40 font-medium">{label}</div><div className="mt-1 text-white text-sm font-medium">{value}</div></div>;}

const PAUSE_REASONS = [
  { code: "MISSING_PART",     label: "Missing Part" },
  { code: "TROUBLESHOOTING",  label: "Troubleshooting" },
  { code: "CUSTOMER_REQUEST", label: "Customer Request" },
  { code: "BIOS_SOFTWARE",    label: "BIOS / Software" },
  { code: "BREAK",            label: "Break" },
  { code: "OTHER",            label: "Other" },
];

function PauseReasonModal({ onCancel, onConfirm, busy }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
      onClick={onCancel}
      data-testid="pause-reason-modal"
    >
      <div
        className="w-full max-w-md rounded-xl border p-6"
        style={{ borderColor: "rgba(255,152,0,0.35)", background: "#0d0d10", boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-1">
          <Pause size={20} style={{ color: "#ff9800" }} />
          <div className="text-white text-lg font-semibold tracking-tight">Pause Build</div>
        </div>
        <div className="text-white/55 text-[12px] mb-5">Choose the reason so IDEA can improve build times.</div>
        <div className="grid grid-cols-2 gap-2.5">
          {PAUSE_REASONS.map((r) => (
            <button
              key={r.code}
              type="button"
              onClick={() => onConfirm(r.code)}
              disabled={busy}
              data-testid={`pause-reason-${r.code.toLowerCase()}`}
              className="h-14 rounded-lg border text-[13px] font-semibold text-white transition-transform hover:-translate-y-[1px] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                borderColor: "rgba(255,152,0,0.4)",
                background: "rgba(255,152,0,0.06)",
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            data-testid="pause-reason-cancel"
            className="px-4 h-10 rounded-md text-[12px] font-semibold tracking-wide text-white/70 hover:text-white border"
            style={{ borderColor: "rgba(255,255,255,0.15)" }}
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
}
